import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Package, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(location.state?.error || null);

  // Check if already authenticated
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setChecking(false);
      return;
    }

    const checkExisting = async () => {
      try {
        const token = localStorage.getItem('kaha_session');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        if (res.data.is_admin) {
          navigate('/admin', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch {
        setChecking(false);
      }
    };
    checkExisting();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem('kaha_auth_return', '/admin');
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-sm w-full shadow-floating" data-testid="admin-login-card">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-accent" />
          </div>
          <h2 className="text-xl font-bold mb-1">Acceso administrativo</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Inicia sesión con tu cuenta de Google autorizada para acceder al panel de administración.
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4" data-testid="login-error">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={handleGoogleLogin}
            className="w-full h-12 gap-3 bg-white hover:bg-gray-50 text-gray-700 border shadow-sm"
            data-testid="btn-google-login"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Iniciar sesión con Google
          </Button>

          <p className="text-xs text-muted-foreground mt-6">
            Solo cuentas autorizadas pueden acceder.
          </p>
        </CardContent>
      </Card>

      <div className="fixed bottom-4 left-4">
        <a href="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
          <Package className="w-5 h-5" />
          <span className="text-sm font-medium">KAHA</span>
        </a>
      </div>
    </div>
  );
};

export default AdminLogin;
